import { track } from "@/utils/conversions";
import {
  AESTHETIC_CAREER_ACCELERATOR_EVENT_DETAIL_ID,
  MID_YEAR_FEATURED_PROGRAMMES,
  MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE,
  MID_YEAR_MASSAGE_COURSE_IDS,
  type MidYearFeaturedProgramme,
} from "@/utils/mid-year-career-upgrade";

export const SUMMER_SPONSORSHIP_CAMPAIGN_SLUG = "summer-sponsorship-2026";
export const SUMMER_SPONSORSHIP_PAGE_PATH =
  "/upgrade-your-career-with-cosmetic-college";

/** First day after the Mid-Year Career Upgrade extension closes. */
export const SUMMER_SPONSORSHIP_START = new Date("2026-07-03T00:00:00+01:00");
export const SUMMER_SPONSORSHIP_END = new Date("2026-08-31T23:59:59+01:00");

export const SUMMER_SPONSORSHIP_HEADLINE =
  "Start this summer, launch by Christmas";

export const SUMMER_SPONSORSHIP_EVENT_NAME = "Summer Sponsorship 2026";

export type SummerSponsorshipPhaseId = "early" | "mid" | "final";

export type SummerSponsorshipPhase = {
  id: SummerSponsorshipPhaseId;
  label: string;
  sponsorshipPercent: 30 | 20 | 15;
  discountCode: "SPONSOR30" | "SPONSOR20" | "SPONSOR15";
  enrollBy: Date;
  enrollByLabel: string;
  startDateWindow: string;
};

export const SUMMER_SPONSORSHIP_PHASES: SummerSponsorshipPhase[] = [
  {
    id: "early",
    label: "Early Summer",
    sponsorshipPercent: 30,
    discountCode: "SPONSOR30",
    enrollBy: new Date("2026-07-19T23:59:59+01:00"),
    enrollByLabel: "Sunday 19 July 2026",
    startDateWindow: "July and August start dates",
  },
  {
    id: "mid",
    label: "Mid Summer",
    sponsorshipPercent: 20,
    discountCode: "SPONSOR20",
    enrollBy: new Date("2026-08-09T23:59:59+01:00"),
    enrollByLabel: "Sunday 9 August 2026",
    startDateWindow: "August and September start dates",
  },
  {
    id: "final",
    label: "Final Call",
    sponsorshipPercent: 20,
    discountCode: "SPONSOR20",
    enrollBy: new Date("2026-08-31T23:59:59+01:00"),
    enrollByLabel: "Monday 31 August 2026",
    startDateWindow: "September start dates",
  },
];

const SPONSORSHIP_ELIGIBLE_PROGRAMME_IDS = new Set([
  "level-5-skin",
  "level-3-access",
  "level-3-body-massage",
  "level-3-indian-head-massage",
  "level-3-aromatherapy-massage",
  "laser",
]);

const NON_SPONSORSHIP_PROGRAMME_OVERRIDES: Record<
  string,
  Pick<MidYearFeaturedProgramme, "priceNote" | "discountCode">
> = {
  "fast-track": {
    priceNote: "£1,250",
    discountCode: null,
  },
};

const MASSAGE_PROGRAMME_IDS = new Set([
  "level-3-body-massage",
  "level-3-indian-head-massage",
  "level-3-aromatherapy-massage",
]);

export const SUMMER_SPONSORSHIP_OFFER_STACK = [
  {
    id: "sponsorship",
    title: "Summer sponsorship on selected programmes",
    detail:
      "Cosmetic College contributes toward your course fees on eligible programmes. The strongest support is available to the earliest enrolments through summer.",
    highlight: "Enrol early",
  },
  {
    id: "outcome",
    title: "Qualified and earning by Christmas",
    detail:
      "Train through the summer, build your skills on models, and be ready for the golden quarter when aesthetics demand peaks.",
    highlight: "Golden quarter ready",
  },
  {
    id: "first-aid",
    title: "First Aid & Anaphylaxis included",
    detail:
      "Eligible sponsored enrolments receive our First Aid & Anaphylaxis training package - a £499 value included to support your readiness.",
    highlight: "£499 included",
  },
  {
    id: "massage-trio",
    title: "VTCT Level 3 massage trio",
    detail:
      "Buy two of our three Level 3 massage awards and get the third free. Add all three to basket and apply L3MASSAGE at checkout.",
    highlight: "Buy 2 get 1 free",
  },
  {
    id: "rpl",
    title: "RPL routes from £999",
    detail:
      "Recognition of Prior Learning for eligible experienced practitioners.",
    highlight: "Experienced route",
  },
  {
    id: "cpd",
    title: "Build your own CPD package",
    detail:
      "Choose four onsite CPD courses from our practical catalogue and checkout at a fixed package price of £999.",
    highlight: "£999",
  },
];

export const SUMMER_SPONSORSHIP_TESTIMONIALS = [
  {
    quote:
      "Such a fantastic course with an amazing tutor Charlotte. I was made to feel so welcome and supported throughout. The pace of the course was perfect, and I feel ready to start treatments.",
    author: "Claire Surridge",
  },
  {
    quote:
      "I would highly recommend Cosmetic College. I have attended two of their courses now and will continue to do so.",
    author: "Stacy Fuller",
  },
];

export function isSummerSponsorshipActive(at = new Date()) {
  return at >= SUMMER_SPONSORSHIP_START && at <= SUMMER_SPONSORSHIP_END;
}

export function isSummerSponsorshipPromotionVisible(at = new Date()) {
  return isSummerSponsorshipActive(at);
}

export function getSummerSponsorshipPhase(
  at = new Date(),
): SummerSponsorshipPhase | null {
  if (!isSummerSponsorshipActive(at)) {
    return null;
  }

  for (const phase of SUMMER_SPONSORSHIP_PHASES) {
    if (at <= phase.enrollBy) {
      return phase;
    }
  }

  return SUMMER_SPONSORSHIP_PHASES[SUMMER_SPONSORSHIP_PHASES.length - 1];
}

export function getSummerSponsorshipPriceNote(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return "";
  }

  return `${phase.sponsorshipPercent}% sponsorship credit - use ${phase.discountCode} at checkout`;
}

export function getSummerSponsorshipNarrativeLede(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return SUMMER_SPONSORSHIP_HEADLINE;
  }

  return `Enrol by ${phase.enrollByLabel} for ${phase.sponsorshipPercent}% sponsorship on selected programmes with ${phase.startDateWindow}.`;
}

export function getSummerSponsorshipHeroBody(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return "Train with us this summer and be qualified and earning in time for the busiest season of the year.";
  }

  return `Cosmetic College contributes up to ${phase.sponsorshipPercent}% of your course fees toward your training on eligible programmes. Enrol by ${phase.enrollByLabel}, choose ${phase.startDateWindow}, and apply code ${phase.discountCode} at checkout.`;
}

export function getSummerSponsorshipMetaDescription(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return `${SUMMER_SPONSORSHIP_HEADLINE}. Summer Sponsorship on selected VTCT and aesthetics programmes at Cosmetic College.`;
  }

  return `Summer Sponsorship 2026: ${phase.sponsorshipPercent}% sponsorship on selected programmes until ${phase.enrollByLabel}. ${SUMMER_SPONSORSHIP_HEADLINE}. First Aid included on eligible routes, RPL from £999.`;
}

export function getSummerSponsorshipWindowBadge(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return SUMMER_SPONSORSHIP_EVENT_NAME;
  }

  return `${phase.label}: ${phase.sponsorshipPercent}% sponsorship until ${phase.enrollByLabel}`;
}

export function getSummerSponsorshipBannerMobile(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return "Summer Sponsorship - train now, earn by Christmas";
  }

  return `Summer Sponsorship: ${phase.sponsorshipPercent}% off selected programmes`;
}

export function getSummerSponsorshipBannerDesktop(
  phase: SummerSponsorshipPhase | null = getSummerSponsorshipPhase(),
) {
  if (!phase) {
    return `${SUMMER_SPONSORSHIP_HEADLINE} - Summer Sponsorship now live`;
  }

  return `${phase.sponsorshipPercent}% Summer Sponsorship until ${phase.enrollByLabel} - ${phase.startDateWindow}`;
}

export function getSummerFeaturedProgrammes(
  at = new Date(),
): MidYearFeaturedProgramme[] {
  const phase = getSummerSponsorshipPhase(at);
  const sponsorshipNote = getSummerSponsorshipPriceNote(phase);

  return MID_YEAR_FEATURED_PROGRAMMES.map((programme) => {
    const updated: MidYearFeaturedProgramme = { ...programme };

    if (programme.id === "massage-trio-bundle") {
      updated.href = `${SUMMER_SPONSORSHIP_PAGE_PATH}#massage-trio`;
      updated.purchaseHref = `${SUMMER_SPONSORSHIP_PAGE_PATH}#massage-trio`;
    }

    if (!phase || !SPONSORSHIP_ELIGIBLE_PROGRAMME_IDS.has(programme.id)) {
      const override = NON_SPONSORSHIP_PROGRAMME_OVERRIDES[programme.id];
      if (override) {
        return { ...updated, ...override };
      }
      return updated;
    }

    if (MASSAGE_PROGRAMME_IDS.has(programme.id)) {
      updated.priceNote = [
        sponsorshipNote,
        `Buy 2 get 1 free on all 3 with ${MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE}`,
      ];
      updated.discountCode = phase.discountCode;
      return updated;
    }

    updated.priceNote = sponsorshipNote;
    updated.discountCode = phase.discountCode;
    return updated;
  });
}

export function getSummerCountdown(at = new Date()) {
  const phase = getSummerSponsorshipPhase(at);

  if (!phase) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, active: false };
  }

  const target = phase.enrollBy.getTime();
  const now = at.getTime();
  const distance = target - now;

  if (distance <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, active: false };
  }

  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((distance % (1000 * 60)) / 1000),
    active: true,
    phase,
  };
}

export function buildSummerCallbackPath(source = SUMMER_SPONSORSHIP_CAMPAIGN_SLUG) {
  return `/request-a-callback/?source=${encodeURIComponent(source)}`;
}

export function buildSummerCheckoutPath(
  discountCode: string | null | undefined = getSummerSponsorshipPhase()?.discountCode,
) {
  if (!discountCode) {
    return "/checkout";
  }

  return `/checkout?code=${encodeURIComponent(discountCode)}`;
}

export function buildSummerQuizPath(campaign = SUMMER_SPONSORSHIP_CAMPAIGN_SLUG) {
  const params = new URLSearchParams();
  params.set("campaign", campaign);
  return `/best-aesthetic-course-for-you/?${params.toString()}`;
}

export function isSummerSponsorshipCampaign(value?: string | string[] | null) {
  if (!value) {
    return false;
  }

  const testValue = Array.isArray(value) ? value[0] : value;
  return testValue === SUMMER_SPONSORSHIP_CAMPAIGN_SLUG;
}

export function trackSummerSponsorshipEvent(
  action: string,
  params: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  const phase = getSummerSponsorshipPhase();

  track(action, {
    event_category: "Summer Sponsorship",
    event_label: SUMMER_SPONSORSHIP_CAMPAIGN_SLUG,
    sponsorship_phase: phase?.id,
    sponsorship_percent: phase?.sponsorshipPercent,
    ...params,
  });

}

export {
  AESTHETIC_CAREER_ACCELERATOR_EVENT_DETAIL_ID,
  MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE,
  MID_YEAR_MASSAGE_COURSE_IDS,
};
